import React from "react";

import {
  FiBox,
  FiCheckCircle,
  FiClipboard,
  FiCreditCard,
  FiEdit3,
  FiMapPin,
  FiPackage,
  FiPhone,
  FiTruck,
  FiUser,
  FiX,
} from "react-icons/fi";

import {
  canCancelOrder,
  canReturnOrder,
  formatCurrency,
  formatDate,
  getOrderItemCount,
  getOrderStatusLabel,
  getPrimaryOrderAction,
  getStatusClass,
} from "../utils/orderHelpers";

import OrderStatusTimeline
  from "./OrderStatusTimeline";


const OrderDetailsDrawer = ({
  open,

  order,

  loading = false,

  actionLoading = false,

  onClose,

  onAccept,

  onChangeStatus,

  onShip,

  onCancel,

  onReturn,

  onEditTracking,

  onEditNotes,
}) => {
  if (!open) {
    return null;
  }


  /* =======================================================
     ACTION
  ======================================================= */

  const primaryAction =
    getPrimaryOrderAction(
      order
    );


  const totalItems =
    getOrderItemCount(
      order
    );


  /* =======================================================
     PRIMARY ACTION
  ======================================================= */

  const handlePrimaryAction =
    () => {
      if (
        !primaryAction ||
        !order
      ) {
        return;
      }


      switch (
        primaryAction.action
      ) {
        case "accept":
          onAccept?.(
            order
          );

          break;


        case "ship":
          onShip?.(
            order
          );

          break;


        default:
          onChangeStatus?.(
            order,
            primaryAction.action
          );

          break;
      }
    };


  return (
    <div
      className="order-drawer-overlay"
    >
      {/* ===================================================
          BACKDROP
      =================================================== */}

      <button
        type="button"
        className="order-drawer-backdrop"
        onClick={
          onClose
        }
        aria-label="Close order details"
      />


      {/* ===================================================
          DRAWER
      =================================================== */}

      <aside
        className="order-drawer"
      >
        {/* =================================================
            HEADER
        ================================================= */}

        <div
          className="order-drawer-header"
        >
          <div>
            <span>
              ORDER DETAILS
            </span>

            <h2>
              {order?.orderNumber ||
                "Order"}
            </h2>

            <div
              className="order-drawer-header-meta"
            >
              <span
                className={`order-status-badge ${getStatusClass(
                  order?.status
                )}`}
              >
                {getOrderStatusLabel(
                  order?.status
                )}
              </span>

              <small>
                {formatDate(
                  order?.createdAt
                )}
              </small>
            </div>
          </div>


          <button
            type="button"
            className="order-drawer-close"
            onClick={
              onClose
            }
            aria-label="Close"
          >
            <FiX />
          </button>
        </div>


        {/* =================================================
            LOADING
        ================================================= */}

        {loading ? (
          <div
            className="order-drawer-loading"
          >
            <span
              className="order-loading-spinner"
            />

            <strong>
              Loading order
            </strong>

            <span>
              Fetching complete
              order details...
            </span>
          </div>
        ) : (
          <>
            {/* =============================================
                BODY
            ============================================= */}

            <div
              className="order-drawer-body"
            >
              {/* ===========================================
                  CUSTOMER
              =========================================== */}

              <section
                className="order-drawer-card"
              >
                <div
                  className="order-drawer-card-heading"
                >
                  <div>
                    <FiUser />

                    <div>
                      <span>
                        CUSTOMER
                      </span>

                      <h3>
                        Customer Details
                      </h3>
                    </div>
                  </div>
                </div>


                <div
                  className="order-drawer-customer"
                >
                  <div
                    className="order-drawer-customer-avatar"
                  >
                    {String(
                      order
                        ?.customer
                        ?.fullName ||
                        "C"
                    )
                      .trim()
                      .charAt(
                        0
                      )
                      .toUpperCase()}
                  </div>


                  <div>
                    <strong>
                      {order
                        ?.customer
                        ?.fullName ||
                        "-"}
                    </strong>

                    <span>
                      <FiPhone />

                      {order
                        ?.customer
                        ?.mobile ||
                        "-"}
                    </span>

                    {order
                      ?.customer
                      ?.email ? (
                      <small>
                        {
                          order
                            .customer
                            .email
                        }
                      </small>
                    ) : null}
                  </div>
                </div>
              </section>


              {/* ===========================================
                  SHIPPING ADDRESS
              =========================================== */}

              <section
                className="order-drawer-card"
              >
                <div
                  className="order-drawer-card-heading"
                >
                  <div>
                    <FiMapPin />

                    <div>
                      <span>
                        DELIVERY
                      </span>

                      <h3>
                        Shipping Address
                      </h3>
                    </div>
                  </div>
                </div>


                <div
                  className="order-address-box"
                >
                  <strong>
                    {order
                      ?.customer
                      ?.fullName ||
                      "-"}
                  </strong>

                  <p>
                    {order
                      ?.customer
                      ?.address ||
                      "-"}

                    {order
                      ?.customer
                      ?.landmark
                      ? `, ${order.customer.landmark}`
                      : ""}
                  </p>

                  <span>
                    {order
                      ?.customer
                      ?.city ||
                      ""}

                    {order
                      ?.customer
                      ?.state
                      ? `, ${order.customer.state}`
                      : ""}

                    {order
                      ?.customer
                      ?.pinCode
                      ? ` - ${order.customer.pinCode}`
                      : ""}
                  </span>
                </div>
              </section>


              {/* ===========================================
                  PRODUCTS
              =========================================== */}

              <section
                className="order-drawer-card"
              >
                <div
                  className="order-drawer-card-heading"
                >
                  <div>
                    <FiPackage />

                    <div>
                      <span>
                        ORDER ITEMS
                      </span>

                      <h3>
                        Products
                      </h3>
                    </div>
                  </div>


                  <strong
                    className="order-drawer-item-count"
                  >
                    {totalItems} item
                    {totalItems === 1
                      ? ""
                      : "s"}
                  </strong>
                </div>


                <div
                  className="order-product-list"
                >
                  {(order?.products ||
                    []).map(
                    (
                      item,
                      index
                    ) => (
                      <div
                        key={
                          item
                            ?.variantId ||
                          item
                            ?.productId ||
                          `${item?.sku}-${index}`
                        }
                        className="order-product-row"
                      >
                        <div
                          className="order-product-image"
                        >
                          {item?.image ? (
                            <img
                              src={
                                `${process.env.REACT_APP_API_URL}/${item.image}`
                              }
                              alt={
                                item.name ||
                                "Product"
                              }
                            />
                          ) : (
                            <FiBox />
                          )}
                        </div>


                        <div
                          className="order-product-info"
                        >
                          <strong>
                            {item?.name ||
                              "-"}
                          </strong>

                          <span>
                            SKU:{" "}
                            {item?.sku ||
                              "-"}
                          </span>


                          {Array.isArray(
                            item
                              ?.selectedOptions
                          ) &&
                          item
                            .selectedOptions
                            .length >
                            0 ? (
                            <div
                              className="order-product-options"
                            >
                              {item.selectedOptions.map(
                                (
                                  option,
                                  optionIndex
                                ) => (
                                  <small
                                    key={`${option?.key}-${optionIndex}`}
                                  >
                                    {option
                                      ?.name ||
                                      option
                                        ?.key}
                                    :{" "}
                                    {
                                      option
                                        ?.value
                                    }
                                  </small>
                                )
                              )}
                            </div>
                          ) : null}
                        </div>


                        <div
                          className="order-product-pricing"
                        >
                          <span>
                            Qty{" "}
                            {item?.qty ||
                              0}
                          </span>

                          <strong>
                            {formatCurrency(
                              item
                                ?.lineTotal
                            )}
                          </strong>

                          <small>
                            {formatCurrency(
                              item
                                ?.finalPrice
                            )}{" "}
                            each
                          </small>
                        </div>
                      </div>
                    )
                  )}
                </div>
              </section>


              {/* ===========================================
                  AMOUNT
              =========================================== */}

              <section
                className="order-drawer-card"
              >
                <div
                  className="order-drawer-card-heading"
                >
                  <div>
                    <FiCreditCard />

                    <div>
                      <span>
                        PAYMENT
                      </span>

                      <h3>
                        Amount Summary
                      </h3>
                    </div>
                  </div>
                </div>


                <div
                  className="order-amount-summary"
                >
                  <div>
                    <span>
                      Subtotal
                    </span>

                    <strong>
                      {formatCurrency(
                        order?.subtotal
                      )}
                    </strong>
                  </div>


                  <div>
                    <span>
                      Discount
                    </span>

                    <strong>
                      -
                      {formatCurrency(
                        order
                          ?.discountAmount
                      )}
                    </strong>
                  </div>


                  <div>
                    <span>
                      Tax
                    </span>

                    <strong>
                      {formatCurrency(
                        order?.taxAmount
                      )}
                    </strong>
                  </div>


                  <div>
                    <span>
                      Delivery
                    </span>

                    <strong>
                      {formatCurrency(
                        order
                          ?.deliveryCharge
                      )}
                    </strong>
                  </div>


                  <div
                    className="total"
                  >
                    <span>
                      Total
                    </span>

                    <strong>
                      {formatCurrency(
                        order
                          ?.totalAmount ||
                          order?.amount
                      )}
                    </strong>
                  </div>
                </div>


                <div
                  className="order-payment-details"
                >
                  <div>
                    <span>
                      Method
                    </span>

                    <strong>
                      {order
                        ?.paymentMethod ===
                      "cod"
                        ? "Cash on Delivery"
                        : "Online Payment"}
                    </strong>
                  </div>


                  <div>
                    <span>
                      Payment Status
                    </span>

                    <strong
                      className={`order-payment-badge ${getStatusClass(
                        order
                          ?.paymentStatus
                      )}`}
                    >
                      {String(
                        order
                          ?.paymentStatus ||
                          "-"
                      ).replace(
                        /_/g,
                        " "
                      )}
                    </strong>
                  </div>
                </div>
              </section>


              {/* ===========================================
                  TRACKING
              =========================================== */}

              <section
                className="order-drawer-card"
              >
                <div
                  className="order-drawer-card-heading"
                >
                  <div>
                    <FiTruck />

                    <div>
                      <span>
                        SHIPPING
                      </span>

                      <h3>
                        Tracking Details
                      </h3>
                    </div>
                  </div>


                  {[
                    "packed",
                    "shipped",
                    "out_for_delivery",
                  ].includes(
                    order?.status
                  ) ? (
                    <button
                      type="button"
                      className="order-drawer-link-button"
                      onClick={() =>
                        onEditTracking?.(
                          order
                        )
                      }
                    >
                      <FiEdit3 />

                      Edit
                    </button>
                  ) : null}
                </div>


                {order
                  ?.tracking
                  ?.trackingNumber ? (
                  <div
                    className="order-tracking-details"
                  >
                    <div>
                      <span>
                        Courier
                      </span>

                      <strong>
                        {order
                          ?.tracking
                          ?.courierName ||
                          "-"}
                      </strong>
                    </div>


                    <div>
                      <span>
                        Tracking / AWB
                      </span>

                      <strong>
                        {order
                          ?.tracking
                          ?.trackingNumber ||
                          "-"}
                      </strong>
                    </div>


                    <div>
                      <span>
                        Estimated Delivery
                      </span>

                      <strong>
                        {formatDate(
                          order
                            ?.tracking
                            ?.estimatedDeliveryDate
                        )}
                      </strong>
                    </div>


                    {order
                      ?.tracking
                      ?.trackingUrl ? (
                      <div
                        className="full"
                      >
                        <span>
                          Tracking URL
                        </span>

                        <a
                          href={
                            order
                              .tracking
                              .trackingUrl
                          }
                          target="_blank"
                          rel="noreferrer"
                        >
                          {
                            order
                              .tracking
                              .trackingUrl
                          }
                        </a>
                      </div>
                    ) : null}


                    {order
                      ?.tracking
                      ?.shippingNote ? (
                      <div
                        className="full"
                      >
                        <span>
                          Shipping Note
                        </span>

                        <p>
                          {
                            order
                              .tracking
                              .shippingNote
                          }
                        </p>
                      </div>
                    ) : null}
                  </div>
                ) : (
                  <div
                    className="order-empty-section"
                  >
                    <FiTruck />

                    <strong>
                      Tracking not added
                    </strong>

                    <span>
                      Courier and tracking
                      information will appear
                      here after shipment is
                      prepared.
                    </span>
                  </div>
                )}
              </section>


              {/* ===========================================
                  TIMELINE
              =========================================== */}

              <section
                className="order-drawer-card"
              >
                <div
                  className="order-drawer-card-heading"
                >
                  <div>
                    <FiCheckCircle />

                    <div>
                      <span>
                        FULFILLMENT
                      </span>

                      <h3>
                        Order Timeline
                      </h3>
                    </div>
                  </div>
                </div>


                <OrderStatusTimeline
                  order={
                    order
                  }
                />
              </section>


              {/* ===========================================
                  NOTES
              =========================================== */}

              <section
                className="order-drawer-card"
              >
                <div
                  className="order-drawer-card-heading"
                >
                  <div>
                    <FiClipboard />

                    <div>
                      <span>
                        INTERNAL
                      </span>

                      <h3>
                        Admin Notes
                      </h3>
                    </div>
                  </div>


                  <button
                    type="button"
                    className="order-drawer-link-button"
                    onClick={() =>
                      onEditNotes?.(
                        order
                      )
                    }
                  >
                    <FiEdit3 />

                    Edit
                  </button>
                </div>


                {order?.notes ? (
                  <p
                    className="order-admin-note"
                  >
                    {order.notes}
                  </p>
                ) : (
                  <div
                    className="order-empty-section compact"
                  >
                    <span>
                      No internal notes added.
                    </span>
                  </div>
                )}
              </section>
            </div>


            {/* =============================================
                FOOTER
            ============================================= */}

            <div
              className="order-drawer-footer"
            >
              {canCancelOrder(
                order
              ) ? (
                <button
                  type="button"
                  className="danger"
                  disabled={
                    actionLoading
                  }
                  onClick={() =>
                    onCancel?.(
                      order
                    )
                  }
                >
                  Cancel Order
                </button>
              ) : null}


              {canReturnOrder(
                order
              ) ? (
                <button
                  type="button"
                  className="return"
                  disabled={
                    actionLoading
                  }
                  onClick={() =>
                    onReturn?.(
                      order
                    )
                  }
                >
                  Return Order
                </button>
              ) : null}


              {primaryAction ? (
                <button
                  type="button"
                  className="primary"
                  disabled={
                    actionLoading
                  }
                  onClick={
                    handlePrimaryAction
                  }
                >
                  {
                    primaryAction.label
                  }
                </button>
              ) : null}
            </div>
          </>
        )}
      </aside>
    </div>
  );
};


export default
  OrderDetailsDrawer;